import { useEditor, useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { styled } from "styled-components";
import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import {
  applyOpacity,
  isRGBA,
  sliderToOpacity
} from "../../../../utils/helpers";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import ContainerEditorSettings from "./ContainerEditorSettings";
import { resolveContainerBackground } from "./containerBackground";
import { lockedMargin } from "./containerFullWidth";
import ContainerEmptyPlaceholder from "./ContainerEmptyPlaceholder";
// NOTE: this rule module is deliberately NOT named "containerEmptyPlaceholder".
// A name differing from ContainerEmptyPlaceholder.jsx only by case resolves to
// the WRONG file on a case-insensitive filesystem (Windows tries .js before
// .jsx), while Linux CI resolves the other one — a silent, platform-dependent
// import swap.
import { shouldShowEmptyPlaceholder } from "./containerEmptyPlaceholderRule";

export const Container = ({
  background,
  containerFill,
  height,
  radius,
  fullWidth,
  spacing,
  margin,
  isApplyPaddingOnAllSides,
  isApplyMarginOnAllSides,
  allSideSpacing,
  allSideMargin,
  opacity,
  border,
  dropShadowColor,
  children,
  ...props
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { container: containerProps } = useSelector((state) => state.codeblockStyles);
  const {getPaletteColor} = usePaletteColor()

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        props.background = props.background || containerProps.background;
        props.height = props.height || containerProps.height;
        props.radius = props.radius || containerProps.radius;
        props.fullWidth = props.fullWidth || containerProps.fullWidth;
        props.spacing = props.spacing || { ...containerProps.spacing };
        props.margin = props.margin || { ...containerProps.margin };
        props.opacity = props.opacity || containerProps.opacity;
        props.border = props.border || containerProps.border;
        props.dropShadowColor =
          props.dropShadowColor || containerProps.dropShadowColor;
      });
    }, 5);

    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  // Reusable hook which applies global settings in each codeblock
  //some keys are different for container codeblock

  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Container);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!background) {
        setProp((props) => {
          props.background = containerProps.background || "#ffffff";
        });
      }

      if (!border?.color) {
        setProp((props) => {
          props.border.color =
            containerProps.border.color || "suggested_color_0";
        });
      }
    }, 10);

    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, [setProp, border, background, containerProps, selectedSegmentColor]);

  const paddingStyle = `${spacing?.top}px ${spacing?.right}px ${spacing?.bottom}px ${spacing?.left}px`

  // GORDON 2026-09-15 (Container block settings pane standardisation, item 1;
  // subsumes gordon-0915-law-full-width-locks-edges-hides-margin-rounding):
  // Full Width locks the container flush to the segment-card edit edges — 0
  // left/right margin — enforced here (not just in the settings-pane write path)
  // so it holds for every container regardless of what is actually stored (e.g.
  // a wheel saved before this law).
  const effectiveMargin = lockedMargin(margin, fullWidth);
  const marginStyle = `${effectiveMargin?.top}px ${effectiveMargin?.right}px ${effectiveMargin?.bottom}px ${effectiveMargin?.left}px`

  const hasChildren = React.Children.count(children) > 0;

  // Sheet R18: an empty Container is near-invisible in the builder (white fill,
  // hairline tinted border, white canvas). Show a dashed, labelled placeholder
  // inside it — but ONLY while the craft.js editor is enabled, so the published
  // Wheel a visitor reads is completely unchanged.
  const { isEditorEnabled } = useEditor((state) => ({
    isEditorEnabled: state.options.enabled,
  }));
  const showEmptyPlaceholder = shouldShowEmptyPlaceholder(
    isEditorEnabled,
    hasChildren
  );

  const StyledPaper = styled.div``;

  // The unchanged legacy solid-color background expression — the ONLY path when
  // the flag is OFF or the container is a plain solid color (byte-identical).
  const legacyBackground = `${applyOpacity(
    getPaletteColor(background),
    sliderToOpacity(opacity)
  )}`;
  // GORDON 2026-09-15 (item 3, standardised Background control): paint a
  // structured Colors/Gradient/Patterns/Image fill via the canonical
  // resolveContainerBackground; a plain solid-color container (no containerFill)
  // resolves to {mode:'legacy'} and takes the expression above. Unconditional —
  // the settings pane's fill control (ContainerFillControl) is always the
  // standard one now, no flag gates it.
  const resolvedBackground = resolveContainerBackground(containerFill);
  const computedBackground =
    resolvedBackground.mode === "fill" ? resolvedBackground.css : legacyBackground;

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <StyledPaper
          {...props}
          style={{
            background: computedBackground,
            minHeight: hasChildren ? "auto" : height ? `${height}vh` : "10vh",
            boxSizing: "border-box",
            padding: paddingStyle,
            borderRadius: `${radius}px`,
            borderColor:
              isRGBA(applyOpacity(getPaletteColor(border?.color), 1)) &&
              applyOpacity(getPaletteColor(border?.color), 1),
            borderImage:
              !isRGBA(applyOpacity(getPaletteColor(border?.color), 1)) &&
              `${applyOpacity(getPaletteColor(border?.color), 1)} 30`,
            borderWidth: `${border?.width}px`,
            borderStyle: "solid",
            boxShadow: dropShadowColor
              ? `0px 4px 12px ${getPaletteColor(dropShadowColor)}`
              : "none",
            margin: marginStyle,
            // Only the empty-in-the-builder case gains a flex box, so the
            // placeholder can stretch to the Container's full height. Every
            // other case (any content, or the published read-only Wheel) keeps
            // the exact style object it had before.
            ...(showEmptyPlaceholder
              ? { display: "flex", alignItems: "stretch" }
              : null),
          }}
        >
          {showEmptyPlaceholder ? <ContainerEmptyPlaceholder /> : children}
        </StyledPaper>
      </BlockIndicator>
    </div>
  );
};

export const ContainerDefaultProps = {};

Container.craft = {
  props: ContainerDefaultProps,
  related: {
    settings: ContainerEditorSettings,
  },
};
